// ============================================================================
// Formula Parser — Tokenizer + Recursive Descent Parser
// Parses calculated field formulas into an AST
// ============================================================================

// ---------------------------------------------------------------------------
// AST Types
// ---------------------------------------------------------------------------

export type ASTNode =
  | { type: 'literal'; value: string | number | boolean | null }
  | { type: 'field'; name: string }
  | { type: 'function'; name: string; args: ASTNode[] }
  | { type: 'binary'; operator: string; left: ASTNode; right: ASTNode }
  | { type: 'unary'; operator: string; operand: ASTNode }
  | {
      type: 'case';
      when: Array<{ condition: ASTNode; result: ASTNode }>;
      else_result?: ASTNode;
    }
  | { type: 'if'; condition: ASTNode; then: ASTNode; else: ASTNode }
  | { type: 'comparison'; operator: string; left: ASTNode; right: ASTNode }
  | { type: 'logical'; operator: 'AND' | 'OR'; left: ASTNode; right: ASTNode }
  | { type: 'not'; operand: ASTNode };

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

export class FormulaParseError extends Error {
  public position: number;
  constructor(message: string, position: number) {
    super(message);
    this.name = 'FormulaParseError';
    this.position = position;
  }
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

type TokenType =
  | 'NUMBER'
  | 'STRING'
  | 'IDENT'
  | 'KEYWORD'
  | 'FUNCTION'
  | 'OPERATOR'
  | 'LPAREN'
  | 'RPAREN'
  | 'COMMA'
  | 'EOF';

interface Token {
  type: TokenType;
  value: string;
  position: number;
}

const KEYWORDS = new Set([
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'IF', 'AND', 'OR', 'NOT',
  'IS', 'NULL', 'TRUE', 'FALSE',
]);

const FUNCTIONS = new Set([
  'CONCAT', 'LOWER', 'UPPER', 'TRIM', 'REPLACE',
  'LEFT', 'RIGHT', 'SUBSTR', 'LENGTH',
  'REGEXP_EXTRACT', 'REGEXP_MATCH', 'REGEXP_REPLACE',
  'YEAR', 'MONTH', 'DAY', 'WEEK',
  'DATE_DIFF', 'FORMAT_DATE',
  'CONTAINS', 'STARTS_WITH', 'ENDS_WITH',
]);

const OPERATORS = ['>=', '<=', '!=', '=', '>', '<', '+', '-', '*', '/'];

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;

  while (pos < input.length) {
    // Skip whitespace
    if (/\s/.test(input[pos])) {
      pos++;
      continue;
    }

    const start = pos;

    // Numbers
    if (/\d/.test(input[pos]) || (input[pos] === '.' && pos + 1 < input.length && /\d/.test(input[pos + 1]))) {
      let num = '';
      while (pos < input.length && /[\d.]/.test(input[pos])) {
        num += input[pos++];
      }
      tokens.push({ type: 'NUMBER', value: num, position: start });
      continue;
    }

    // Strings (double or single quoted)
    if (input[pos] === '"' || input[pos] === "'") {
      const quote = input[pos++];
      let str = '';
      while (pos < input.length && input[pos] !== quote) {
        if (input[pos] === '\\' && pos + 1 < input.length) {
          pos++;
          str += '\\' + input[pos++];
        } else {
          str += input[pos++];
        }
      }
      if (pos >= input.length) {
        throw new FormulaParseError(`Unterminated string literal`, start);
      }
      pos++; // closing quote
      tokens.push({ type: 'STRING', value: str, position: start });
      continue;
    }

    // Identifiers / keywords / functions
    if (/[a-zA-Z_]/.test(input[pos])) {
      let ident = '';
      while (pos < input.length && /[a-zA-Z0-9_]/.test(input[pos])) {
        ident += input[pos++];
      }
      const upper = ident.toUpperCase();
      if (FUNCTIONS.has(upper)) {
        tokens.push({ type: 'FUNCTION', value: upper, position: start });
      } else if (KEYWORDS.has(upper)) {
        tokens.push({ type: 'KEYWORD', value: upper, position: start });
      } else {
        tokens.push({ type: 'IDENT', value: ident, position: start });
      }
      continue;
    }

    // Multi-char operators
    const twoChar = input.slice(pos, pos + 2);
    if (OPERATORS.includes(twoChar)) {
      tokens.push({ type: 'OPERATOR', value: twoChar, position: start });
      pos += 2;
      continue;
    }

    // Single-char operators
    const oneChar = input[pos];
    if (OPERATORS.includes(oneChar)) {
      tokens.push({ type: 'OPERATOR', value: oneChar, position: start });
      pos++;
      continue;
    }

    // Punctuation
    if (oneChar === '(') {
      tokens.push({ type: 'LPAREN', value: '(', position: start });
      pos++;
      continue;
    }
    if (oneChar === ')') {
      tokens.push({ type: 'RPAREN', value: ')', position: start });
      pos++;
      continue;
    }
    if (oneChar === ',') {
      tokens.push({ type: 'COMMA', value: ',', position: start });
      pos++;
      continue;
    }

    throw new FormulaParseError(`Unexpected character: '${oneChar}'`, pos);
  }

  tokens.push({ type: 'EOF', value: '', position: pos });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser (recursive descent)
// ---------------------------------------------------------------------------
// Precedence (low to high):
//   OR < AND < NOT < comparison (=, !=, >, <, >=, <=, IS NULL, IS NOT NULL)
//   < addition (+, -) < multiplication (*, /) < unary (-, NOT) < atom
// ---------------------------------------------------------------------------

class Parser {
  private tokens: Token[];
  private pos: number;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
    this.pos = 0;
  }

  private current(): Token {
    return this.tokens[this.pos];
  }

  private advance(): Token {
    const tok = this.tokens[this.pos];
    this.pos++;
    return tok;
  }

  private expect(type: TokenType, value?: string): Token {
    const tok = this.current();
    if (tok.type !== type || (value !== undefined && tok.value !== value)) {
      const expected = value ? `'${value}'` : type;
      throw new FormulaParseError(
        `Expected ${expected} but got '${tok.value}'`,
        tok.position,
      );
    }
    return this.advance();
  }

  private match(type: TokenType, value?: string): boolean {
    const tok = this.current();
    return tok.type === type && (value === undefined || tok.value === value);
  }

  // Entry
  parse(): ASTNode {
    const node = this.parseOr();
    if (!this.match('EOF')) {
      throw new FormulaParseError(
        `Unexpected token '${this.current().value}'`,
        this.current().position,
      );
    }
    return node;
  }

  // OR
  private parseOr(): ASTNode {
    let left = this.parseAnd();
    while (this.match('KEYWORD', 'OR')) {
      this.advance();
      const right = this.parseAnd();
      left = { type: 'logical', operator: 'OR', left, right };
    }
    return left;
  }

  // AND
  private parseAnd(): ASTNode {
    let left = this.parseNot();
    while (this.match('KEYWORD', 'AND')) {
      this.advance();
      const right = this.parseNot();
      left = { type: 'logical', operator: 'AND', left, right };
    }
    return left;
  }

  // NOT
  private parseNot(): ASTNode {
    if (this.match('KEYWORD', 'NOT')) {
      this.advance();
      const operand = this.parseNot();
      return { type: 'not', operand };
    }
    return this.parseComparison();
  }

  // Comparison: =, !=, >, <, >=, <=, IS NULL, IS NOT NULL
  private parseComparison(): ASTNode {
    let left = this.parseAddition();

    // IS NULL / IS NOT NULL
    if (this.match('KEYWORD', 'IS')) {
      this.advance();
      if (this.match('KEYWORD', 'NOT')) {
        this.advance();
        this.expect('KEYWORD', 'NULL');
        return { type: 'comparison', operator: 'IS NOT NULL', left, right: { type: 'literal', value: null } };
      }
      this.expect('KEYWORD', 'NULL');
      return { type: 'comparison', operator: 'IS NULL', left, right: { type: 'literal', value: null } };
    }

    const compOps = ['=', '!=', '>', '<', '>=', '<='];
    if (this.match('OPERATOR') && compOps.includes(this.current().value)) {
      const op = this.advance().value;
      const right = this.parseAddition();
      return { type: 'comparison', operator: op, left, right };
    }

    return left;
  }

  // Addition / Subtraction
  private parseAddition(): ASTNode {
    let left = this.parseMultiplication();
    while (this.match('OPERATOR', '+') || this.match('OPERATOR', '-')) {
      const op = this.advance().value;
      const right = this.parseMultiplication();
      left = { type: 'binary', operator: op, left, right };
    }
    return left;
  }

  // Multiplication / Division
  private parseMultiplication(): ASTNode {
    let left = this.parseUnary();
    while (this.match('OPERATOR', '*') || this.match('OPERATOR', '/')) {
      const op = this.advance().value;
      const right = this.parseUnary();
      left = { type: 'binary', operator: op, left, right };
    }
    return left;
  }

  // Unary minus
  private parseUnary(): ASTNode {
    if (this.match('OPERATOR', '-')) {
      this.advance();
      const operand = this.parseUnary();
      return { type: 'unary', operator: '-', operand };
    }
    return this.parseAtom();
  }

  // Atom: literals, fields, functions, CASE, IF, parenthesized expressions
  private parseAtom(): ASTNode {
    const tok = this.current();

    // Number literal
    if (tok.type === 'NUMBER') {
      this.advance();
      const num = tok.value.includes('.') ? parseFloat(tok.value) : parseInt(tok.value, 10);
      return { type: 'literal', value: num };
    }

    // String literal
    if (tok.type === 'STRING') {
      this.advance();
      return { type: 'literal', value: tok.value };
    }

    // Boolean / NULL keywords
    if (tok.type === 'KEYWORD' && tok.value === 'TRUE') {
      this.advance();
      return { type: 'literal', value: true };
    }
    if (tok.type === 'KEYWORD' && tok.value === 'FALSE') {
      this.advance();
      return { type: 'literal', value: false };
    }
    if (tok.type === 'KEYWORD' && tok.value === 'NULL') {
      this.advance();
      return { type: 'literal', value: null };
    }

    // CASE WHEN ... THEN ... [ELSE ...] END
    if (tok.type === 'KEYWORD' && tok.value === 'CASE') {
      return this.parseCaseExpression();
    }

    // IF(condition, then, else) — IF is a keyword
    if (tok.type === 'KEYWORD' && tok.value === 'IF') {
      return this.parseIfExpression();
    }

    // Function call
    if (tok.type === 'FUNCTION') {
      return this.parseFunctionCall();
    }

    // Identifier (field reference)
    if (tok.type === 'IDENT') {
      this.advance();
      return { type: 'field', name: tok.value };
    }

    // Parenthesized expression
    if (tok.type === 'LPAREN') {
      this.advance();
      const expr = this.parseOr();
      this.expect('RPAREN');
      return expr;
    }

    throw new FormulaParseError(
      `Unexpected token '${tok.value}'`,
      tok.position,
    );
  }

  private parseCaseExpression(): ASTNode {
    this.expect('KEYWORD', 'CASE');
    const whenClauses: Array<{ condition: ASTNode; result: ASTNode }> = [];

    while (this.match('KEYWORD', 'WHEN')) {
      this.advance();
      const condition = this.parseOr();
      this.expect('KEYWORD', 'THEN');
      const result = this.parseOr();
      whenClauses.push({ condition, result });
    }

    if (whenClauses.length === 0) {
      throw new FormulaParseError(
        `CASE requires at least one WHEN clause`,
        this.current().position,
      );
    }

    let else_result: ASTNode | undefined;
    if (this.match('KEYWORD', 'ELSE')) {
      this.advance();
      else_result = this.parseOr();
    }

    this.expect('KEYWORD', 'END');

    return { type: 'case', when: whenClauses, else_result };
  }

  private parseIfExpression(): ASTNode {
    this.expect('KEYWORD', 'IF');
    this.expect('LPAREN');
    const condition = this.parseOr();
    this.expect('COMMA');
    const thenExpr = this.parseOr();
    this.expect('COMMA');
    const elseExpr = this.parseOr();
    this.expect('RPAREN');
    return { type: 'if', condition, then: thenExpr, else: elseExpr };
  }

  private parseFunctionCall(): ASTNode {
    const nameTok = this.advance(); // FUNCTION token
    this.expect('LPAREN');
    const args: ASTNode[] = [];

    if (!this.match('RPAREN')) {
      args.push(this.parseOr());
      while (this.match('COMMA')) {
        this.advance();
        args.push(this.parseOr());
      }
    }

    this.expect('RPAREN');
    return { type: 'function', name: nameTok.value, args };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function parseFormula(input: string): ASTNode {
  const tokens = tokenize(input);
  const parser = new Parser(tokens);
  return parser.parse();
}
